import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMPTY = { name: '', category: '', unit: '', price: '', weight_lb: '', size_note: '', image_url: '', farm_id: '', is_active: true };

export default function AdminBuildBoxItems() {
  const [items, setItems] = useState([]);
  const [farms, setFarms] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const [i, f] = await Promise.all([api.get('/admin/build-box/items'), api.get('/admin/roster-farms')]);
    setItems(i.data);
    setFarms(f.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setForm(EMPTY); };
  const openEdit = (it) => { setEditingId(it.item_id); setForm({ ...it, price: String(it.price), weight_lb: String(it.weight_lb) }); };

  const save = async () => {
    if (!form.farm_id) { toast.error('Select an approved farm as the confirmed source'); return; }
    const payload = { ...form, price: parseFloat(form.price), weight_lb: parseFloat(form.weight_lb) };
    try {
      if (editingId) await api.put(`/admin/build-box/items/${editingId}`, payload);
      else await api.post('/admin/build-box/items', payload);
      toast.success(editingId ? 'Item updated' : 'Item created');
      setForm(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save item');
    }
  };

  const remove = async (itemId) => {
    await api.delete(`/admin/build-box/items/${itemId}`);
    toast.success('Item removed');
    load();
  };

  return (
    <div data-testid="admin-build-box-items">
      <div className="flex items-center justify-between mb-6">
        <p className="font-manrope text-dark/60 max-w-xl">Individual items customers can add to a Build Your Own Box. Every item must have an approved farm as its confirmed source before it becomes visible to customers.</p>
        <button onClick={openNew} className="btn-primary py-2.5 px-5 text-sm whitespace-nowrap" data-testid="new-build-box-item-btn"><Plus className="w-4 h-4 mr-1" />Add Item</button>
      </div>

      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        {items.length === 0 ? <p className="p-12 text-center font-manrope text-dark/50">No build-your-own-box items yet.</p> : (
          <div className="divide-y divide-forest/10">
            {items.map((it) => (
              <div key={it.item_id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`build-box-item-row-${it.item_id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2"><h3 className="font-playfair text-lg font-bold text-dark">{it.name}</h3>{!it.is_active && <span className="badge-local text-xs">Inactive</span>}</div>
                  <p className="font-manrope text-sm text-dark/60">{it.category} · ${it.price}/{it.unit} · {it.weight_lb} lb · {it.farm_name || 'No confirmed source'}</p>
                </div>
                <button onClick={() => openEdit(it)} className="btn-secondary py-2 px-4 text-sm" data-testid={`edit-build-box-item-${it.item_id}`}><Pencil className="w-4 h-4 mr-1" />Edit</button>
                <button onClick={() => remove(it.item_id)} className="p-2 text-dark/40 hover:text-red-500" data-testid={`delete-build-box-item-${it.item_id}`}><Trash2 className="w-5 h-5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="bg-cream max-w-lg">
          {form && (
            <>
              <DialogHeader><DialogTitle className="font-playfair text-2xl">{editingId ? 'Edit Item' : 'New Item'}</DialogTitle><DialogDescription>Items require a confirmed, approved farm source before customers can see them.</DialogDescription></DialogHeader>
              <div className="space-y-3 font-manrope text-sm">
                <div><Label>Name</Label><Input className="bg-white border-forest/20" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label><Input className="bg-white border-forest/20" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Vegetables" data-testid="item-category" /></div>
                  <div><Label>Unit</Label><Input className="bg-white border-forest/20" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="bunch" data-testid="item-unit" /></div>
                  <div><Label>Price ($)</Label><Input type="number" className="bg-white border-forest/20" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="item-price" /></div>
                  <div><Label>Weight (lb)</Label><Input type="number" className="bg-white border-forest/20" value={form.weight_lb} onChange={(e) => setForm({ ...form, weight_lb: e.target.value })} data-testid="item-weight" /></div>
                </div>
                <div><Label>Size note (optional)</Label><Input className="bg-white border-forest/20" value={form.size_note} onChange={(e) => setForm({ ...form, size_note: e.target.value })} placeholder="1 quart" data-testid="item-size-note" /></div>
                <div><Label>Image URL</Label><Input className="bg-white border-forest/20" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="item-image-url" /></div>
                <div>
                  <Label>Confirmed farm source</Label>
                  <select value={form.farm_id} onChange={(e) => setForm({ ...form, farm_id: e.target.value })} className="bg-white border border-forest/20 rounded-xl px-3 py-2 w-full" data-testid="item-farm-select">
                    <option value="">— Select an approved farm —</option>
                    {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{`${f.name} — ${f.town || 'NH'}`}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="item-is-active" />Active (visible to customers)</label>
                <button onClick={save} className="btn-primary py-2 px-5 text-sm" data-testid="save-build-box-item">Save Item</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
